import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Users2, ShieldCheck, ScrollText, UserCheck, ArrowRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PanelPendientes from "@/components/PanelPendientes";
import KimKimScrapeWidget from "@/components/KimKimScrapeWidget";

const fmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("es-ES", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

const ACTION_LABEL = {
  login: "Inicio de sesión", create_user: "Alta de usuario", update_user: "Edición de usuario",
  delete_user: "Baja de usuario", reset_password: "Reseteo de contraseña",
  change_password: "Cambio de contraseña", update_role: "Edición de rol",
};

const KpiCard = ({ icon: Icon, label, value, sub, loading }) => (
  <Card data-testid={`kpi-${label}`}>
    <CardContent className="flex items-center gap-4 p-5">
      <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
        {loading ? <Skeleton className="mt-1 h-7 w-16" /> : <p className="text-2xl font-bold tabular-nums">{value}</p>}
        {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
      </div>
    </CardContent>
  </Card>
);

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;

  const usersQ = useQuery({ queryKey: ["users"], queryFn: async () => (await api.get("/users")).data, enabled: isAdmin });
  const rolesQ = useQuery({ queryKey: ["roles"], queryFn: async () => (await api.get("/roles")).data, enabled: isAdmin });
  const auditQ = useQuery({ queryKey: ["audit", "recent"], queryFn: async () => (await api.get("/audit?limit=8")).data, enabled: isAdmin });

  const users = usersQ.data || [];
  const activeUsers = users.filter((u) => u.active).length;
  const logs = auditQ.data?.logs || [];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight">Hola, {user?.full_name?.split(" ")[0]} 👋</h1>
        <p className="text-sm text-muted-foreground">{isAdmin ? "Panel de control · tus pendientes y administración del sistema." : "Tu panel de inicio · acciones pendientes de hoy."}</p>
      </div>

      <PanelPendientes />

      {isAdmin && <KimKimScrapeWidget />}

      {isAdmin && (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard icon={Users2} label="Usuarios" value={users.length} sub="en el sistema" loading={usersQ.isLoading} />
        <KpiCard icon={UserCheck} label="Activos" value={activeUsers} sub="pueden iniciar sesión" loading={usersQ.isLoading} />
        <KpiCard icon={ShieldCheck} label="Roles" value={(rolesQ.data || []).length} sub="perfiles configurados" loading={rolesQ.isLoading} />
        <KpiCard icon={ScrollText} label="Eventos" value={logs.length} sub="actividad reciente" loading={auditQ.isLoading} />
      </div>
      )}

      {isAdmin && (
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base">Actividad reciente</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate("/app/auditoria")} data-testid="goto-audit">
              Ver auditoría <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </CardHeader>
          <CardContent className="space-y-1">
            {auditQ.isLoading && Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
            {!auditQ.isLoading && logs.length === 0 && (
              <p className="py-6 text-center text-sm text-muted-foreground">Sin actividad todavía.</p>
            )}
            {logs.map((l) => (
              <div key={l.id} className="flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-muted/60">
                <Badge variant="secondary" className="shrink-0 text-[10px]">{ACTION_LABEL[l.action] || l.action}</Badge>
                <span className="min-w-0 flex-1 truncate text-sm">{l.detail || "—"}</span>
                <span className="shrink-0 text-xs text-muted-foreground">{l.user_email}</span>
                <span className="shrink-0 text-xs tabular-nums text-muted-foreground">{fmt(l.timestamp)}</span>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Accesos rápidos</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            <Button variant="outline" className="w-full justify-between" onClick={() => navigate("/app/usuarios")} data-testid="goto-users">
              <span className="flex items-center gap-2"><Users2 className="h-4 w-4" /> Gestionar usuarios</span>
              <ArrowRight className="h-4 w-4" />
            </Button>
            <Button variant="outline" className="w-full justify-between" onClick={() => navigate("/app/roles_perms")} data-testid="goto-roles">
              <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> Roles y permisos</span>
              <ArrowRight className="h-4 w-4" />
            </Button>
            <Button variant="outline" className="w-full justify-between" onClick={() => navigate("/app/auditoria")} data-testid="goto-audit-2">
              <span className="flex items-center gap-2"><ScrollText className="h-4 w-4" /> Auditoría</span>
              <ArrowRight className="h-4 w-4" />
            </Button>
          </CardContent>
        </Card>
      </div>
      )}
    </div>
  );
}
